import { LegacyInput, InputRef } from './input'
import { Ref, forwardRef, useEffect, RefObject } from 'react'
import { uiEvents } from '@/utils/event'
import { useVenueContext } from '@/services/venue'
import { CountryCode, NationalNumber } from 'libphonenumber-js/types'
import { useAppContext } from '@/pages/_app'
import { parsePhoneNumberFromString, getExampleNumber } from 'libphonenumber-js/max'
import { INPUT_PHONE_KEY, INPUT_PHONE_STORAGE } from './input-phone-storage'

const phoneExamples: { [key in CountryCode]: NationalNumber } = {
  ...require('libphonenumber-js/examples.mobile.json'),
  GB: '2012345678',
  RU: '9261234567',
}

declare global {
  interface AppUIEventMap {
    InputPhoneChanged: {
      type: 'InputPhoneChanged'
      newValue: string
    }
  }
}

export type InputPhoneProps = {
  label: string
  required?: boolean
}
export const InputPhone = forwardRef(InputPhoneWithRef)
function InputPhoneWithRef(props: InputPhoneProps, ref: Ref<InputRef>) {
  const { t } = useAppContext()
  const { venue } = useVenueContext()!

  useEffect(() => {
    const input = (ref as RefObject<InputRef>)?.current
    if (input && input.value.length > 0) {
      input?.touch()
    }
  }, [])

  const venueCountryCode = venue.country.toUpperCase() as CountryCode
  const getCountryCodeByInput = (input: string) => {
    if (input.startsWith('+')) {
      return undefined
    }

    return venueCountryCode
  }

  return (
    <LegacyInput
      ref={ref}
      schemaVersion={2}
      id={INPUT_PHONE_KEY}
      label={props.label}
      storage={INPUT_PHONE_STORAGE}
      type="tel"
      value=""
      required={!!props.required}
      validateOn="blur"
      validate={(input) => {
        const countryCode = getCountryCodeByInput(input)
        const phoneNumber = parsePhoneNumberFromString(input, countryCode)
        const numberIsValid =
          phoneNumber &&
          phoneNumber.isValid() &&
          (!countryCode || phoneNumber.country === countryCode)

        if (input.length > 0 && !numberIsValid) {
          const example = countryCode ? getExampleNumber(countryCode, phoneExamples)! : null

          return [
            example
              ? t('inputPhone:correctFormat', {
                  example: example.formatNational().replace(/[^\d]/g, ''),
                })
              : t('inputPhone:invalidFormat'),
          ]
        }

        return []
      }}
      onChange={(input) => {
        const strippedValue = input.replace(/[^\d\+]/g, '')

        const countryCode = getCountryCodeByInput(strippedValue)
        const newValue = strippedValue
        uiEvents.next({
          type: 'InputPhoneChanged',
          newValue,
        })
        return [newValue, parsePhoneNumberFromString(input, countryCode)?.number]
      }}
    />
  )
}
